import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { ApiError, confirmPasswordReset, requestPasswordReset } from '../../api';

const inputCls =
  'bg-field text-fg placeholder:text-fg-subtle focus:ring-brand-600 w-full rounded-lg border border-default px-4 py-2 outline-none focus:border-transparent focus:ring-2';
const labelCls = 'mb-1 block text-sm font-semibold text-fg-secondary';

interface Props {
  onBackToLogin: () => void;
  /** Prefill the email (e.g. from a worker invite link). */
  initialEmail?: string;
  /** Start directly at the code/new-password step (the invite already emailed a code). */
  initialStep?: 'request' | 'confirm';
  /** Reword copy for a new-worker invite rather than a password reset. */
  inviteMode?: boolean;
}

/**
 * Two-step password flow for salon owners and workers on the /admin login screen.
 * Backend endpoints are role-agnostic — same /auth/password/reset-request/ and
 * /auth/password/reset-confirm/ used by customers. Worker invites reuse the same
 * reset-password code, so this doubles as the invitee "set your password" screen.
 */
export default function AdminPasswordReset({
  onBackToLogin,
  initialEmail,
  initialStep,
  inviteMode = false,
}: Props) {
  const [step, setStep] = useState<'request' | 'confirm'>(initialStep ?? 'request');
  const [email, setEmail] = useState(initialEmail ?? '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);

  const errMessage = (err: unknown, fallback: string) =>
    err instanceof ApiError ? err.message : fallback;

  const resendCode = async () => {
    setError('');
    setBusy(true);
    try {
      await requestPasswordReset(email.trim());
      setInfo('A new code has been sent to your email.');
    } catch (err) {
      setError(errMessage(err, 'Could not send a code.'));
    } finally {
      setBusy(false);
    }
  };

  const submitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await requestPasswordReset(email.trim());
      setInfo('If that email is registered, a reset code has been sent.');
      setStep('confirm');
    } catch (err) {
      setError(errMessage(err, 'Could not send a reset code.'));
    } finally {
      setBusy(false);
    }
  };

  const submitConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await confirmPasswordReset(email.trim(), code.trim(), newPassword);
      setInfo(
        inviteMode ? 'Password set. You can now log in.' : 'Password reset. You can now log in.',
      );
      onBackToLogin();
    } catch (err) {
      setError(errMessage(err, 'Invalid or expired code.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={step === 'request' ? submitRequest : submitConfirm} className="space-y-4">
      <p className="text-fg-muted text-sm">
        {step === 'request'
          ? 'Enter your account email and we will send you a reset code.'
          : inviteMode
            ? 'Enter the code from your invite email and choose a password.'
            : 'Enter the code we emailed you and choose a new password.'}
      </p>
      {error && (
        <div className="border-danger/25 bg-danger-soft text-danger rounded-xl border p-3 text-sm">
          {error}
        </div>
      )}
      {info && (
        <div className="border-success/30 bg-success-soft text-success rounded-xl border p-3 text-sm">
          {info}
        </div>
      )}
      <div>
        <label className={labelCls} htmlFor="admin-reset-email">
          Email
        </label>
        <input
          id="admin-reset-email"
          type="email"
          className={inputCls}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          disabled={step === 'confirm'}
        />
      </div>
      {step === 'confirm' && (
        <>
          <div>
            <label className={labelCls} htmlFor="admin-reset-code">
              Reset code
            </label>
            <input
              id="admin-reset-code"
              className={`${inputCls} tracking-[0.3em]`}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              placeholder="000000"
              required
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="admin-reset-pw">
              New password
            </label>
            <input
              id="admin-reset-pw"
              type="password"
              className={inputCls}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="admin-reset-pw-confirm">
              Confirm new password
            </label>
            <input
              id="admin-reset-pw-confirm"
              type="password"
              className={inputCls}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
          </div>
          {inviteMode ? (
            // A worker's email can also belong to a customer account; a self-serve
            // "resend" would mail a reset code to the wrong account. The salon owner
            // resends invites instead (admin → Staff → Resend invite).
            <p className="text-fg-muted text-xs">
              Didn&rsquo;t receive your invite code? Ask your salon to resend it.
            </p>
          ) : (
            <p className="text-fg-muted text-xs">
              Didn&rsquo;t receive the code?{' '}
              <button
                type="button"
                onClick={resendCode}
                disabled={busy}
                className="text-accent hover:underline disabled:opacity-50"
              >
                Send a new code
              </button>
              .
            </p>
          )}
        </>
      )}
      <button
        type="submit"
        disabled={busy}
        className="bg-brand-600 hover:bg-brand-700 flex w-full items-center justify-center gap-2 rounded-xl py-3 font-bold text-white transition-colors disabled:opacity-50"
      >
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        {step === 'request' ? 'Send reset code' : 'Reset password'}
      </button>
      <div className="text-center text-sm">
        <button type="button" className="text-accent hover:underline" onClick={onBackToLogin}>
          Back to log in
        </button>
      </div>
    </form>
  );
}
